/* FURVIA theme — 3D engine (vanilla Three.js port of the FurLift storefront) */
import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';

const FINISHES = { Pink: '#EDC7CF', Blue: '#BDD9E7', Green: '#C8E0CE', White: '#F5F1E9' };
const FINISH_NAMES = { Pink: 'Blush Pink', Blue: 'Sky Blue', Green: 'Sage Green', White: 'Ivory White' };

const clamp01 = (v) => Math.min(1, Math.max(0, v));
const smooth = (t) => t * t * (3 - 2 * t);
const lerp = (a, b, t) => a + (b - a) * t;

function weaveTexture() {
  const c = document.createElement('canvas');
  c.width = c.height = 512;
  const ctx = c.getContext('2d');
  ctx.fillStyle = '#46464a';
  ctx.fillRect(0, 0, 512, 512);
  for (let i = 0; i < 2600; i++) {
    const x = Math.random() * 512, y = Math.random() * 512;
    const l = 6 + Math.random() * 14;
    const a = Math.random() > 0.5 ? Math.PI / 4 : -Math.PI / 4;
    const g = 130 + Math.random() * 100;
    ctx.strokeStyle = `rgba(${g},${g},${g},0.5)`;
    ctx.lineWidth = 1.1;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + Math.cos(a) * l, y + Math.sin(a) * l);
    ctx.stroke();
  }
  for (let y = 0; y < 512; y += 7) {
    ctx.fillStyle = 'rgba(0,0,0,0.10)';
    ctx.fillRect(0, y, 512, 2);
  }
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.anisotropy = 4;
  return tex;
}

function shadowTexture() {
  const c = document.createElement('canvas');
  c.width = c.height = 256;
  const ctx = c.getContext('2d');
  const g = ctx.createRadialGradient(128, 128, 10, 128, 128, 120);
  g.addColorStop(0, 'rgba(40,30,16,0.42)');
  g.addColorStop(1, 'rgba(40,30,16,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 256, 256);
  return new THREE.CanvasTexture(c);
}

class FurLiftModel {
  constructor() {
    this.group = new THREE.Group();
    this.exploded = 0;
    this.targetExploded = 0;
    this.highlight = null;
    this.targetColor = new THREE.Color(FINISHES.Pink);

    const weave = weaveTexture();
    this.matTop = new THREE.MeshPhysicalMaterial({ color: FINISHES.Pink, roughness: 0.42, metalness: 0.02, clearcoat: 0.55, clearcoatRoughness: 0.4, transparent: true });
    this.matBottom = this.matTop.clone();
    this.matPad = new THREE.MeshStandardMaterial({ map: weave, roughness: 0.85, metalness: 0.25, transparent: true });
    this.matMetal = new THREE.MeshStandardMaterial({ color: '#d9d4cb', metalness: 0.85, roughness: 0.3, transparent: true });
    this.matDark = new THREE.MeshStandardMaterial({ color: '#2c2a27', roughness: 0.6, transparent: true });
    this.bodyMats = [this.matTop, this.matBottom];
    this.allMats = [this.matTop, this.matBottom, this.matPad, this.matMetal, this.matDark];

    this.bottom = new THREE.Group();
    this.bottom.position.set(0, -0.5, 0);
    this.bottom.add(new THREE.Mesh(new RoundedBoxGeometry(0.58, 1.05, 0.46, 6, 0.14), this.matBottom));
    this.group.add(this.bottom);

    this.top = new THREE.Group();
    this.top.position.set(0, 0.42, 0);
    this.top.add(new THREE.Mesh(new RoundedBoxGeometry(0.6, 1.0, 0.5, 6, 0.16), this.matTop));
    const recess = new THREE.Mesh(new THREE.BoxGeometry(0.37, 0.62, 0.04), this.matDark);
    recess.position.set(0, 0, 0.235);
    this.top.add(recess);
    const backplate = new THREE.Mesh(new RoundedBoxGeometry(0.44, 0.8, 0.05, 4, 0.02), this.matMetal);
    backplate.position.set(0, 0, -0.26);
    this.top.add(backplate);
    this.group.add(this.top);

    this.pad = new THREE.Group();
    this.pad.position.set(0, 0.42, 0.26);
    this.pad.add(new THREE.Mesh(new THREE.BoxGeometry(0.32, 0.56, 0.045), this.matPad));
    this.group.add(this.pad);

    this.wiper = new THREE.Group();
    this.wiper.position.set(0, 0.86, 0);
    const stem = new THREE.Mesh(new RoundedBoxGeometry(0.11, 0.16, 0.13, 5, 0.04), this.matTop);
    stem.position.set(0, 0.04, 0);
    const bar = new THREE.Mesh(new RoundedBoxGeometry(0.24, 0.075, 0.46, 5, 0.033), this.matTop);
    bar.position.set(0, 0.12, 0);
    this.wiper.add(stem, bar);
    this.group.add(this.wiper);
  }

  setColor(color) {
    this.targetColor.set(FINISHES[color] || FINISHES.Pink);
  }

  update(dt) {
    const k = Math.min(1, dt * 4);
    this.exploded += (this.targetExploded - this.exploded) * k;
    const e = this.exploded;
    this.bodyMats.forEach((m) => m.color.lerp(this.targetColor, k));

    const hl = this.highlight;
    const gold = new THREE.Color(0xC5A46D);
    const black = new THREE.Color(0x000000);
    this.allMats.forEach((m) => {
      const isGold = hl === 'finish';
      let dim = 1;
      if (hl && hl !== 'finish') {
        const partMats = { surface: [this.matPad], mechanism: [this.matMetal], body: [this.matTop, this.matBottom] }[hl] || [];
        dim = partMats.includes(m) ? 1 : 0.15;
      }
      m.opacity += (dim - m.opacity) * k;
      const target = isGold ? 0.5 : (hl && dim === 1 ? 0.14 : 0);
      m.emissiveIntensity += (target - m.emissiveIntensity) * k;
      m.emissive.copy(isGold || (hl && dim === 1) ? gold : black);
    });

    this.wiper.position.y = 0.86 + e * 0.78;
    this.top.position.y = 0.42 + e * 0.38;
    this.bottom.position.y = -0.5 - e * 0.5;
    this.pad.position.y = 0.42 + e * 0.15 + (this.padWave || 0);
    this.pad.position.z = 0.26 + e * 0.75;
  }
}

function makeShadow(size, opacity) {
  const m = new THREE.Mesh(
    new THREE.PlaneGeometry(size, size),
    new THREE.MeshBasicMaterial({ map: shadowTexture(), transparent: true, depthWrite: false, opacity })
  );
  m.rotation.x = -Math.PI / 2;
  return m;
}

function baseLights(scene, warm) {
  scene.add(new THREE.AmbientLight(new THREE.Color('#fff6ea'), warm ? 0.85 : 0.7));
  const hemi = new THREE.HemisphereLight(new THREE.Color('#fff7ec'), new THREE.Color(warm ? '#cbb79b' : '#181614'), 0.6);
  scene.add(hemi);
  const key = new THREE.DirectionalLight(new THREE.Color('#fff1dc'), warm ? 1.5 : 1.6);
  key.position.set(3, 4, 5);
  scene.add(key);
  const rim = new THREE.DirectionalLight(new THREE.Color('#e8cfd7'), warm ? 0.55 : 0.7);
  rim.position.set(-4, 1.5, -3);
  scene.add(rim);
  const spot = new THREE.SpotLight(new THREE.Color('#ffffff'), warm ? 1.2 : 1.4, 0, 0.5, 1);
  spot.position.set(0, 7, 3);
  scene.add(spot);
}

function makeRenderer(container) {
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.75));
  renderer.setSize(container.clientWidth, container.clientHeight);
  container.appendChild(renderer.domElement);
  return renderer;
}

/* ---------- HERO ---------- */
function initHero(container, onExplore) {
  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(30, container.clientWidth / container.clientHeight, 0.1, 50);
  camera.position.set(0, 0.15, 5.1);
  const renderer = makeRenderer(container);
  baseLights(scene, true);

  const model = new FurLiftModel();
  const rig = new THREE.Group();
  rig.add(model.group);
  scene.add(rig);

  const shadow = makeShadow(3.4, 0.85);
  shadow.position.y = -1.45;
  scene.add(shadow);

  let px = 0, py = 0;
  const raycaster = new THREE.Raycaster();
  const pointerV = new THREE.Vector2();
  window.addEventListener('pointermove', (e) => {
    px = (e.clientX / window.innerWidth) * 2 - 1;
    py = (e.clientY / window.innerHeight) * 2 - 1;
  }, { passive: true });

  container.addEventListener('pointerdown', (e) => {
    const r = container.getBoundingClientRect();
    pointerV.set(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
    raycaster.setFromCamera(pointerV, camera);
    const hits = raycaster.intersectObject(model.group, true);
    if (hits.length && onExplore) onExplore();
  });

  const clock = new THREE.Clock();
  let running = true;
  function frame() {
    if (!running) return;
    requestAnimationFrame(frame);
    const dt = clock.getDelta();
    const t = clock.elapsedTime;
    const wide = window.innerWidth > 768;
    rig.position.set(wide ? 1.15 : 0, wide ? 0 : 0.39, 0);
    const s = wide ? 0.88 : 0.6;
    rig.scale.set(s, s, s);
    rig.rotation.y += (Math.sin(t * 0.25) * 0.28 + px * 0.5 - rig.rotation.y) * Math.min(1, dt * 3);
    rig.rotation.x += (-py * 0.12 - rig.rotation.x) * Math.min(1, dt * 3);
    model.group.position.y = Math.sin(t * 1.4) * 0.06;
    model.setColor(window.Furvia?.color || 'Pink');
    model.update(dt);
    renderer.render(scene, camera);
  }
  frame();

  window.addEventListener('resize', () => {
    camera.aspect = container.clientWidth / container.clientHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(container.clientWidth, container.clientHeight);
  });

  window.Furvia3D = window.Furvia3D || {};
  window.Furvia3D.pauseHero = function (v) { running = !v; if (!v) { clock.getDelta(); frame(); } };
}

/* ---------- FINISHES ---------- */
function initFinishes(container) {
  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(30, container.clientWidth / container.clientHeight, 0.1, 50);
  camera.position.set(0, 0.1, 4.2);
  const renderer = makeRenderer(container);
  baseLights(scene, true);
  const model = new FurLiftModel();
  const rig = new THREE.Group();
  rig.rotation.y = 0.55;
  rig.add(model.group);
  scene.add(rig);
  const shadow = makeShadow(3.2, 0.7);
  shadow.position.y = -1.6;
  scene.add(shadow);
  const clock = new THREE.Clock();
  (function frame() {
    requestAnimationFrame(frame);
    const dt = clock.getDelta();
    const t = clock.elapsedTime;
    rig.rotation.y = 0.55 + Math.sin(t * 0.3) * 0.15;
    model.group.position.y = Math.sin(t * 1.2) * 0.05;
    model.setColor(window.Furvia?.color || 'Pink');
    model.update(dt);
    renderer.render(scene, camera);
  })();
  window.addEventListener('resize', () => {
    camera.aspect = container.clientWidth / container.clientHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(container.clientWidth, container.clientHeight);
  });
}

/* ---------- STORY ---------- */
const KF = [
  { at: 0.0, cam: [0, 0.1, 4.6], rotY: 0.6, x: 0, tilt: 0 },
  { at: 0.15, cam: [0, 0.05, 4.4], rotY: 2.9, x: 0, tilt: 0 },
  { at: 0.3, cam: [0.2, 0.55, 2.2], rotY: 0.1, x: 0, tilt: -0.12 },
  { at: 0.46, cam: [0, 0.5, 2.5], rotY: Math.PI + 0.15, x: 0, tilt: 0 },
  { at: 0.6, cam: [1.1, 0.2, 3.2], rotY: 0.5, x: -1.0, tilt: 0 },
  { at: 0.78, cam: [0, 0.15, 3.0], rotY: 0.3, x: 0, tilt: 0 },
  { at: 1.0, cam: [0, 0.1, 4.4], rotY: 0.62, x: 0, tilt: 0 },
];

function pose(p) {
  let i = 0;
  while (i < KF.length - 2 && p > KF[i + 1].at) i++;
  const a = KF[i], b = KF[i + 1];
  const t = smooth(clamp01((p - a.at) / (b.at - a.at || 1)));
  return {
    cam: [lerp(a.cam[0], b.cam[0], t), lerp(a.cam[1], b.cam[1], t), lerp(a.cam[2], b.cam[2], t)],
    rotY: lerp(a.rotY, b.rotY, t),
    x: lerp(a.x, b.x, t),
    tilt: lerp(a.tilt, b.tilt, t),
  };
}

function initStory(container) {
  const scene = new THREE.Scene();
  scene.background = new THREE.Color('#181614');
  const camera = new THREE.PerspectiveCamera(30, container.clientWidth / container.clientHeight, 0.1, 50);
  camera.position.set(0, 0.1, 4.6);
  const renderer = makeRenderer(container);
  baseLights(scene, false);
  const model = new FurLiftModel();
  const rig = new THREE.Group();
  rig.add(model.group);
  scene.add(rig);
  const shadow = makeShadow(3.6, 1);
  shadow.position.y = -1.55;
  scene.add(shadow);

  window.Furvia3D = window.Furvia3D || {};
  window.Furvia3D.storyProgress = 0;

  const clock = new THREE.Clock();
  (function frame() {
    requestAnimationFrame(frame);
    const dt = clock.getDelta();
    const p = window.Furvia3D.storyProgress || 0;
    const ps = pose(p);
    rig.rotation.y = ps.rotY;
    rig.rotation.x = ps.tilt;
    rig.position.x = ps.x;
    camera.position.x += (ps.cam[0] - camera.position.x) * 0.09;
    camera.position.y += (ps.cam[1] - camera.position.y) * 0.09;
    camera.position.z += (ps.cam[2] - camera.position.z) * 0.09;
    camera.lookAt(0, 0, 0);
    const t5 = clamp01((p - 0.62) / 0.16);
    model.padWave = Math.sin(Math.PI * t5) * 0.8;
    model.setColor(window.Furvia?.color || 'Pink');
    model.update(dt);
    renderer.render(scene, camera);
  })();
  window.addEventListener('resize', () => {
    camera.aspect = container.clientWidth / container.clientHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(container.clientWidth, container.clientHeight);
  });
}

/* ---------- EXPLORER ---------- */
const EX_DEFAULT = { rotY: 0.62, camX: 0, camY: 0.1, dist: 4.3 };
const EX_HOTSPOTS = [
  { id: 'surface', label: 'Collection Surface', num: '01', pos: [0, 0.45, 0.42], rotY: 0.12, cam: [0, 0.45, 2.2], title: 'The Lift Weave', desc: 'A precision-woven micro-loop pad grips stray hair from fabric in one slow pass — no adhesives, no refills, nothing to throw away.' },
  { id: 'mechanism', label: 'Self-Cleaning Blade', num: '02', pos: [0, 0.75, -0.42], rotY: Math.PI + 0.1, cam: [0, 0.5, 2.3], title: 'The Self-Cleaning Blade', desc: 'Behind the pad, a spring-tension wiper waits. One pull strips every trapped strand straight off the pad.' },
  { id: 'body', label: 'Compact Body', num: '03', pos: [0, -0.62, 0.42], rotY: 0.35, cam: [0, -0.45, 2.4], title: 'The Body', desc: 'Two matte shells, balanced in the hand. Quiet, light, and made to glide along fabric — not fight it.' },
  { id: 'finish', label: 'The Finishes', num: '04', pos: [0, 1.14, 0.25], rotY: 0.6, cam: [0, 0.95, 2.6], title: 'Four Quiet Finishes', desc: 'Blush Pink, Sky Blue, Sage Green, Ivory White — powder-soft mattes chosen to disappear into a beautiful home.' },
];
const EX_PARTS = [
  { id: 'wiper', label: 'Self-Cleaning Wiper', num: '01', pos: [0, 1.64, 0], hl: 'body', desc: 'The T-wiper shears the pad clean in a single pull — collected hair releases in one motion.' },
  { id: 'pad', label: 'Lift Pad', num: '02', pos: [0, 0.57, 1.05], hl: 'surface', desc: 'The woven collection surface. It grips hair from deep within the fibers of any fabric.' },
  { id: 'top', label: 'Shell & Backplate', num: '03', pos: [0, 0.8, -0.55], hl: 'mechanism', desc: 'The upper shell carries the pad window and a rigid backplate that keeps the pad taut and true.' },
  { id: 'bottom', label: 'Handle Shell', num: '04', pos: [0, -1.05, 0], hl: 'body', desc: 'The lower shell keeps every strand out of sight until you release it.' },
];

function initExplorer(overlay, stage) {
  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(32, stage.clientWidth / stage.clientHeight, 0.1, 50);
  camera.position.set(0, 0.1, 9);
  const renderer = makeRenderer(stage);
  baseLights(scene, false);
  const model = new FurLiftModel();
  const rig = new THREE.Group();
  rig.position.set(0, -0.05, 0);
  rig.add(model.group);
  scene.add(rig);
  const shadow = makeShadow(3.6, 1);
  shadow.position.y = -1.65;
  scene.add(shadow);

  const markers = overlay.querySelectorAll('.fv-hotspot, .fv-part');
  const card = overlay.querySelector('.fv-card');
  let active = null;
  let exploded = false;
  let interacted = false;
  const target = { ...EX_DEFAULT };
  const intro = { t0: null, done: false };
  let last = null;
  const clock = new THREE.Clock();

  function setCard(def) {
    if (!card) return;
    if (!def) { card.classList.remove('fv-card--on'); return; }
    card.querySelector('[data-ex-card-num]').textContent = def.num || '';
    card.querySelector('[data-ex-card-title]').textContent = def.title || def.label;
    card.querySelector('[data-ex-card-desc]').textContent = def.desc;
    card.classList.add('fv-card--on');
  }

  markers.forEach((el) => {
    el.addEventListener('pointerdown', (e) => e.stopPropagation());
    el.addEventListener('click', () => {
      const id = el.getAttribute('data-ex');
      if (active === id) { active = null; setCard(null); return; }
      active = id;
      if (!exploded) {
        const hs = EX_HOTSPOTS.find((h) => h.id === id);
        if (hs) { target.rotY = hs.rotY; target.camX = hs.cam[0]; target.camY = hs.cam[1]; target.dist = hs.cam[2]; }
        setCard(hs);
        model.highlight = id;
      } else {
        const part = EX_PARTS.find((p) => p.hl === id || p.id === id);
        if (part) { setCard(part); model.highlight = part.hl; }
      }
    });
  });

  const closeBtn = overlay.querySelector('[data-ex-close]');
  if (closeBtn) closeBtn.addEventListener('click', close);
  const resetBtn = overlay.querySelector('[data-ex-reset]');
  if (resetBtn) resetBtn.addEventListener('click', () => {
    Object.assign(target, EX_DEFAULT);
    active = null;
    model.highlight = null;
    setCard(null);
    intro.done = true;
    rig.rotation.y = EX_DEFAULT.rotY;
  });
  const explBtn = overlay.querySelector('[data-ex-exploded]');
  if (explBtn) explBtn.addEventListener('click', () => {
    exploded = !exploded;
    model.targetExploded = exploded ? 1 : 0;
    active = null;
    model.highlight = null;
    setCard(null);
    explBtn.querySelector('span').textContent = exploded ? 'Reassemble' : 'Discover the Design';
  });
  const dismiss = overlay.querySelector('.fv-card__dismiss');
  if (dismiss) dismiss.addEventListener('click', () => { active = null; model.highlight = null; setCard(null); });

  stage.addEventListener('pointerdown', (e) => { interacted = true; last = { x: e.clientX, y: e.clientY }; stage.setPointerCapture(e.pointerId); });
  stage.addEventListener('pointermove', (e) => {
    if (!last || !intro.done) return;
    target.rotY += (e.clientX - last.x) * 0.0065;
    last = { x: e.clientX, y: e.clientY };
  });
  stage.addEventListener('pointerup', () => { last = null; });
  stage.addEventListener('wheel', (e) => {
    e.preventDefault();
    interacted = true;
    if (intro.done) target.dist = Math.min(6.5, Math.max(2.1, target.dist + e.deltaY * 0.0016 * target.dist));
  }, { passive: false });

  const v = new THREE.Vector3();
  function project() {
    const size = { w: stage.clientWidth, h: stage.clientHeight };
    const list = exploded ? EX_PARTS : EX_HOTSPOTS;
    markers.forEach((el) => {
      const id = el.getAttribute('data-ex');
      const def = list.find((d) => d.id === id || d.hl === id);
      if (!def) { el.style.display = 'none'; return; }
      v.set(def.pos[0], def.pos[1], def.pos[2]).applyMatrix4(rig.matrixWorld).project(camera);
      const x = (v.x * 0.5 + 0.5) * size.w;
      const y = (-v.y * 0.5 + 0.5) * size.h;
      el.style.display = v.z < 1 ? (el.classList.contains('fv-part') ? 'inline-block' : 'flex') : 'none';
      el.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%)`;
    });
  }

  let running = false;
  function frame() {
    if (!running) return;
    requestAnimationFrame(frame);
    const dt = clock.getDelta();
    const t = clock.getElapsedTime();

    if (!intro.done) {
      if (intro.t0 === null) intro.t0 = t;
      const p = Math.min(1, (t - intro.t0) / 1.9);
      const e = 1 - Math.pow(1 - p, 3);
      rig.rotation.y = -Math.PI + e * (EX_DEFAULT.rotY + Math.PI);
      camera.position.set(0, 0.1, 9 - e * (9 - EX_DEFAULT.dist));
      camera.lookAt(0, 0, 0);
      if (p >= 1) { intro.done = true; Object.assign(target, EX_DEFAULT); }
    } else {
      const hs = EX_HOTSPOTS.find((h) => h.id === active);
      if (hs) { target.rotY = hs.rotY; target.camX = hs.cam[0]; target.camY = hs.cam[1]; target.dist = hs.cam[2]; }
      else if (exploded) target.dist = Math.max(target.dist, 5.9);
      const k = Math.min(1, dt * 5);
      rig.rotation.y += (target.rotY - rig.rotation.y) * k;
      rig.rotation.x += (0 - rig.rotation.x) * k;
      camera.position.x += (target.camX - camera.position.x) * k;
      camera.position.y += (target.camY - camera.position.y) * k;
      camera.position.z += (target.dist - camera.position.z) * k;
      camera.lookAt(0, 0, 0);
    }

    rig.updateMatrixWorld(true);
    project();
    model.setColor(window.Furvia?.color || 'Pink');
    model.update(dt);
    renderer.render(scene, camera);
  }

  function open() {
    overlay.classList.add('fv-explorer--on');
    document.body.style.overflow = 'hidden';
    if (window.__lenis) window.__lenis.stop();
    running = true;
    intro.t0 = null;
    intro.done = false;
    clock.getDelta();
    frame();
  }
  function close() {
    overlay.classList.remove('fv-explorer--on');
    document.body.style.overflow = '';
    if (window.__lenis) window.__lenis.start();
    running = false;
  }
  window.addEventListener('resize', () => {
    camera.aspect = stage.clientWidth / stage.clientHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(stage.clientWidth, stage.clientHeight);
  });
  return { open, close };
}

/* ---------- boot ---------- */
document.addEventListener('DOMContentLoaded', () => {
  const heroCanvas = document.querySelector('.fv-hero__canvas');
  const explorerOverlay = document.querySelector('.fv-explorer');
  let explorer = null;

  try {
    if (explorerOverlay) explorer = initExplorer(explorerOverlay, explorerOverlay.querySelector('.fv-explorer__stage'));
    if (heroCanvas) {
      initHero(heroCanvas, () => { if (explorer) { explorer.open(); if (window.Furvia3D) window.Furvia3D.pauseHero && window.Furvia3D.pauseHero(true); } });
    }
    const exploreBtn = document.querySelector('[data-fv-explore]');
    if (exploreBtn && explorer) exploreBtn.addEventListener('click', () => explorer.open());
    const exClose = explorerOverlay ? explorerOverlay.querySelector('[data-ex-close]') : null;

    const finishes = document.querySelector('.fv-finishes__panel');
    if (finishes) initFinishes(finishes);
    const story = document.querySelector('.fv-story__canvas');
    if (story) initStory(story);
  } catch (err) {
    console.warn('Furvia 3D unavailable:', err);
    if (heroCanvas) heroCanvas.innerHTML = '<img src="' + (heroCanvas.getAttribute('data-fallback') || '') + '" alt="FurLift" style="width:100%;height:100%;object-fit:contain;">';
  }
});
